import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { seoConfig, getSiteUrl, SITE_IMAGE, SITE_NAME } from './seoConfig';

function setMeta(attr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function removeMeta(attr: 'name' | 'property', key: string) {
  const el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (el) el.remove();
}

function setCanonical(url: string) {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', url);
}

export default function SeoManager() {
  const location = useLocation();

  useEffect(() => {
    const path = location.pathname;
    const siteUrl = getSiteUrl();
    const seo = seoConfig[path];

    // 未收录路由（404 等）不做 SEO 优化，并标记为不收录
    if (!seo) {
      document.title = `页面未找到 - ${SITE_NAME}`;
      setMeta('name', 'robots', 'noindex, nofollow');
      // 清理上一个路由遗留的 SEO 标签，避免元数据泄漏
      removeMeta('name', 'description');
      removeMeta('name', 'keywords');
      removeMeta('property', 'og:title');
      removeMeta('property', 'og:description');
      removeMeta('property', 'og:type');
      removeMeta('property', 'og:url');
      removeMeta('property', 'og:image');
      removeMeta('property', 'og:site_name');
      removeMeta('name', 'twitter:card');
      removeMeta('name', 'twitter:title');
      removeMeta('name', 'twitter:description');
      removeMeta('name', 'twitter:image');
      const canonical = document.head.querySelector<HTMLLinkElement>(
        'link[rel="canonical"]'
      );
      if (canonical) canonical.remove();
      return;
    }

    removeMeta('name', 'robots');
    const url = `${siteUrl}${path === '/' ? '/' : path}`;

    document.title = seo.title;
    setMeta('name', 'description', seo.description);
    setMeta('name', 'keywords', seo.keywords);
    setCanonical(url);

    setMeta('property', 'og:title', seo.title);
    setMeta('property', 'og:description', seo.description);
    setMeta('property', 'og:type', 'website');
    setMeta('property', 'og:url', url);
    setMeta('property', 'og:image', SITE_IMAGE);
    setMeta('property', 'og:site_name', SITE_NAME);

    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', seo.title);
    setMeta('name', 'twitter:description', seo.description);
    setMeta('name', 'twitter:image', SITE_IMAGE);
  }, [location.pathname]);

  return null;
}